// askSmart-ui/src/pages/TOSP/tospTraffic.ts
//
// What the three lights above the tabs are counting.
//
// The same idea as the other modules' traffic lights, worked out from TOSP's
// own records rather than shared with them: red is the admin's own unfinished
// work, yellow is what a tenant owes and hasn't sent.
//
// Everything here is derived on the spot from records already loaded — nothing
// is stored, so a light can never be stale or need clearing. Fix the thing and
// the row goes.

import type { TospTab } from "./ManageTOSP";
import { monthName, SALES_DUE_DAY } from "./portal/portalSummary";
import { monthsOwing, TenantSalesMonth } from "./portal/tenantSales";
import {
  activeOccupancyOf,
  currentAllocation,
  currentTenants,
  formatSpan,
  lapsedTenancies,
  occupancyEndDate,
  Occupancy,
  TaxRollRecord,
  Tenant,
  tenantLabel,
  tenancyEndDate,
  tenantOpeningDate,
  tenantReportsSales,
  spanBetween,
  todayISO,
  TospConfiguration,
  Unit,
} from "./types";

export type TrafficLevel = "red" | "yellow" | "green";

/** One thing to do, and the tab it's done on. */
export interface TrafficTask {
  id: string;
  detail: string;
  tab: TospTab;
  /**
   * A record the destination tab should open on arrival.
   *
   * For a task whose answer is a decision rather than a form — "has this tenant
   * left?" — landing on the tab is not enough: the admin would have to find the
   * row that raised it. The tab reads this and reopens the question itself.
   */
  focus?: string;
}

/** The tasks that belong together — a tenant, or Configuration itself. */
export interface TrafficGroup {
  id: string;
  name: string;
  tasks: TrafficTask[];
}

export const taskCount = (groups: TrafficGroup[]): number =>
  groups.reduce((total, group) => total + group.tasks.length, 0);

// ── Red: the admin's own unfinished work ────────────────────────────────────

/**
 * Configuration that has been left at its default of "not stated".
 *
 * A null rate isn't a zero rate — the fields are deliberately null until
 * somebody states a figure, so the portfolio keeps whatever each record was
 * saved with. That is the right failure, but it is silent, and a rate nobody
 * ever got round to entering looks exactly like one that doesn't apply.
 */
function configurationTasks(configuration: TospConfiguration): TrafficTask[] {
  const tasks: TrafficTask[] = [];
  const add = (id: string, detail: string) =>
    tasks.push({ id: `config:${id}`, detail, tab: "configuration" });

  if (configuration.bmvaRatePerSqFt === null) {
    add("bmvaRate", "BMVA rate per sq ft not set");
  }
  if (configuration.bmvaRoyaltyPercentage === null) {
    add("bmvaRoyalty", "BMVA royalty percentage not set");
  }
  if (configuration.wasteTotalCost === null) {
    add("wasteCost", "Waste removal total cost not set");
  }

  // An empty list is a dropdown with nothing in it — the form that reads it
  // can't be completed at all. Read defensively: a configuration saved before
  // a list existed simply doesn't carry it, and the light must survive that.
  const list = (values: string[] | undefined) => values || [];
  if (list(configuration.retailerTypes).length === 0) {
    add("retailerTypes", "No Retailer Types committed");
  }
  if (list(configuration.spaceTypes).length === 0) {
    add("spaceTypes", "No Space Types committed");
  }
  if (list(configuration.wasteRemovalTypes).length === 0) {
    add("wasteTypes", "No Waste Removal Types committed");
  }

  // An empty list here doesn't read as "not configured yet" anywhere else — it
  // reads as "no space attracts percentage rent", which silently zeroes every
  // breakpoint in the portfolio.
  if (list(configuration.percentageRentSpaceTypes).length === 0) {
    add("percentageRentSpaceTypes", "No Space Type attracts percentage rent");
  }

  // A category with no split can't be apportioned when the budget is built.
  // Ones marked ignored are deliberate — a header or an exempt row — and are
  // not an omission.
  for (const category of configuration.camCategories || []) {
    const split = category.split;
    if (split?.ignored) continue;
    if (!split?.splitAmong || !split.basedOn || (split.entities || []).length === 0) {
      add(`cam:${category.id}`, `CAM category "${category.name}" has no split defined`);
    }
  }

  return tasks;
}

/** Whether a lease is still running — an ended one can no longer be chased. */
function isCurrent(occupancy: Occupancy): boolean {
  const end = occupancyEndDate(occupancy);
  return !end || end >= todayISO();
}

/**
 * Fields left blank on a tenant or one of its current leases.
 *
 * Only what an admin can actually act on. A blank that the record legitimately
 * carries — no deposit required, no exclusive clause — must not appear here,
 * or the light stays red forever and stops being read.
 */
function tenantTasks(tenant: Tenant): TrafficTask[] {
  const tasks: TrafficTask[] = [];
  const add = (id: string, detail: string) =>
    tasks.push({ id: `tenant:${tenant.id}:${id}`, detail, tab: "tenants" });

  if (!tenant.retailerType.trim()) add("retailerType", "Business Type not set");
  if (!tenant.wasteRemoval.trim()) add("wasteRemoval", "Waste Removal not set");
  if (!tenant.primaryContactEmail.trim()) {
    // Without it there is no portal login and no tenant at all, as far as the
    // rest of the module is concerned — the tenancy is matched on this address.
    add("contactEmail", "No primary contact email — the tenant has no portal access");
  }
  if (!tenant.primaryContactPhone.trim()) add("contactPhone", "No primary contact phone");

  // Prepaid rent, like the deposit: a date is only asked for when an amount
  // was actually prepaid. Zero or blank asks for nothing.
  if ((tenant.prepaidRentAmount ?? 0) > 0 && !tenant.prepaidRentDate) {
    add("prepaidDate", "Prepaid rent received date not recorded");
  }

  const leases = tenant.occupancies || [];
  const current = leases.filter(isCurrent);
  if (leases.length === 0) {
    add("noLease", "No lease recorded");
  }

  current.forEach((occupancy, index) => {
    // Named by position rather than by id: a tenant with one lease shouldn't
    // be told which of them, and one with several needs telling them apart.
    const which = current.length > 1 ? ` (lease ${index + 1})` : "";
    const on = (id: string, detail: string) => add(`${occupancy.id}:${id}`, `${detail}${which}`);

    // The one the deposit turns on: the lease says an amount is owed, and
    // nothing on the record says it ever arrived.
    //
    // The tenant's date counts. The Date Paid box on the tenant form saves to
    // the TENANT, and nothing writes the lease's own date — so reading only
    // the lease flagged every tenant whose date had in fact been entered.
    //
    // The deposit asks TWO questions in sequence, and never both at once.
    //
    // First: what does the lease say? A blank figure is an unanswered question,
    // not an absence — nobody has yet read the lease and recorded what it calls
    // for. So the gap to chase is the AMOUNT, and the message says how to record
    // "none", because entering 0.00 to mean prepaid rent is not something an
    // admin would guess at.
    //
    // Then, only once there is a figure above zero: when did it arrive? An
    // explicit 0.00 ends the matter — some leases call for prepaid rent instead
    // of a deposit, and asking for the date a payment of nothing was received
    // leaves a light red for ever.
    //
    // The DATE is read off the tenant as well as the lease. The Date Paid box on
    // the tenant form saves to the tenant, and nothing writes the lease's own
    // date, so reading only the lease flagged every tenant whose date had in fact
    // been entered. The AMOUNT is read off the lease alone: it is a lease term
    // now, and the tenant's legacy figure outvoting an explicit 0.00 was what
    // kept flagging tenancies the lease had already answered for.
    const leaseDeposit = occupancy.securityDepositAmount;
    if (leaseDeposit === null || leaseDeposit === undefined) {
      on(
        "depositAmount",
        "Security deposit amount not recorded — enter 0.00 if the lease calls for prepaid rent instead",
      );
    } else if (leaseDeposit > 0) {
      const depositDate = occupancy.depositPaidDate || tenant.depositPaidDate;
      if (!depositDate) on("depositDate", "Security deposit received date not recorded");
    }
    if (occupancy.leasedSpaces.length === 0) {
      on("spaces", "No unit assigned to the lease");
    }
    if (!occupancy.leaseCommenceDate) {
      on("commence", "No lease commencement date — renewal dates can't be worked out");
    } else if (!occupancy.leaseTerm.trim()) {
      // Only worth saying once: without the commence date the term can't
      // produce a date either, and two rows for one gap reads as two problems.
      on("term", "No lease term — renewal dates can't be worked out");
    }
    if (!occupancy.leaseReview) {
      on("review", "Lease has never been reviewed against the document");
    }
  });

  return tasks;
}

/** Everything waiting on the admin, Configuration first. */
export function redGroups(
  configuration: TospConfiguration,
  tenants: Tenant[],
  /** Optional so callers that only want the tenant and configuration rules
   *  — the ones written before the register existed — still compile. */
  taxRolls: TaxRollRecord[] = [],
  units: Unit[] = [],
): TrafficGroup[] {
  const groups: TrafficGroup[] = [];

  const config = configurationTasks(configuration);
  if (config.length > 0) {
    groups.push({ id: "configuration", name: "Configuration", tasks: config });
  }

  // Grouped apart from the tenants, because neither belongs to one: an
  // unassigned roll has no tenant yet by definition, and an unheld unit is the
  // absence of a tenancy rather than a fault in one.
  const rolls = taxRollTasks(taxRolls);
  if (rolls.length > 0) {
    groups.push({ id: "taxRolls", name: "Tax Rolls", tasks: rolls });
  }

  const unassigned = unitTasks(units, tenants);
  if (unassigned.length > 0) {
    groups.push({ id: "units", name: "Units", tasks: unassigned });
  }

  // Only the businesses still trading. A gap in a departed tenancy's record is
  // history, not work: nobody is going to go back and fill in the contact
  // details of a shop that closed two years ago, so flagging it red for ever
  // would only teach the admin to ignore the light.
  // A lease that has run out and nobody has ruled on. Grouped with the tenant's
  // own tasks rather than apart, because it IS about that tenant — and carried
  // as a `focus` so Go reopens the question instead of dropping the admin on the
  // Overview to find the row again.
  const lapsedByTenant = new Map<string, TrafficTask>();
  for (const lapsed of lapsedTenancies(tenants)) {
    if (lapsedByTenant.has(lapsed.tenant.id)) continue;
    lapsedByTenant.set(lapsed.tenant.id, {
      id: `lapsed:${lapsed.tenant.id}:${lapsed.occupancy.id}`,
      detail:
        lapsed.daysOver === 0
          ? "Lease ends today and no renewal is recorded — have they left?"
          : `Lease ended ${formatSpan(spanBetween(lapsed.endedOn, todayISO()) ?? { years: 0, days: lapsed.daysOver })} ago and no renewal is recorded — have they left?`,
      tab: "overview" as TospTab,
      focus: lapsed.tenant.id,
    });
  }

  for (const tenant of currentTenants(tenants)) {
    const tasks = [...tenantTasks(tenant)];
    const lapsed = lapsedByTenant.get(tenant.id);
    // First in the group: whether the tenancy still exists outranks a gap in
    // its details.
    if (lapsed) tasks.unshift(lapsed);
    if (tasks.length > 0) {
      groups.push({
        id: tenant.id,
        name: tenantLabel(tenant) || "Unnamed tenant",
        tasks,
      });
    }
  }

  return groups;
}

/**
 * Roll shares assigned to nothing.
 *
 * A parcel is assessed and billed whether or not anybody has said which unit
 * holds it, so an unassigned share is tax the centre pays and recovers from
 * nobody. Only the CURRENT allocation counts — an earlier year's division is
 * history and is not waiting on anyone.
 */
function taxRollTasks(rolls: TaxRollRecord[]): TrafficTask[] {
  const tasks: TrafficTask[] = [];
  for (const roll of rolls) {
    const current = currentAllocation(roll);
    for (const part of current.parts) {
      if (part.assignedTo) continue;
      tasks.push({
        id: `roll:${roll.id}:${part.id}`,
        detail:
          // A part's label is "A", "B", "C" … and is blank while the roll is
          // whole, so a split roll says which share and an undivided one just
          // names the roll.
          current.parts.length > 1 && part.label
            ? `Roll ${roll.rollNumber} part ${part.label} is not assigned to a unit`
            : `Roll ${roll.rollNumber} is not assigned to a unit`,
        tab: "taxRolls",
      });
    }
  }
  return tasks;
}

/**
 * Units no lease demises.
 *
 * There are no vacancies in this portfolio, so a unit nobody holds is a lease
 * that hasn't been recorded rather than empty space — and it is rent, BMVA fees
 * and a tax share the budget is silently short. A lapsed term still counts as
 * held: only an explicit end date gives a unit back (see isOccupancyActive).
 */
function unitTasks(units: Unit[], tenants: Tenant[]): TrafficTask[] {
  return units
    .filter((unit) => !activeOccupancyOf(unit.id, tenants))
    .map((unit) => ({
      id: `unit:${unit.id}`,
      detail: `Suite ${unit.suiteNumber} is not assigned to any tenant`,
      tab: "units" as TospTab,
    }));
}

/**
 * How many red tasks each tab is carrying.
 *
 * Read off the tasks themselves rather than counted again here — every task
 * already names the tab it is fixed on, and a second set of rules would be one
 * more thing to keep in step with the first.
 */
export function redCountsByTab(groups: TrafficGroup[]): Partial<Record<TospTab, number>> {
  const counts: Partial<Record<TospTab, number>> = {};
  for (const group of groups) {
    for (const task of group.tasks) {
      counts[task.tab] = (counts[task.tab] || 0) + 1;
    }
  }
  return counts;
}

// ── Yellow: what the tenants owe ────────────────────────────────────────────

/**
 * Sales months past their due date and still unreported, tenant by tenant.
 *
 * The same reckoning the tenant sees on their own portal — one function, so
 * the two can't disagree about which months are outstanding. Oldest first
 * within a tenant; the tenant furthest behind first overall, since the row
 * that needs chasing hardest should be the one at the top.
 */
export function yellowGroups(
  tenants: Tenant[],
  sales: TenantSalesMonth[],
  now: Date = new Date(),
): TrafficGroup[] {
  const groups: TrafficGroup[] = [];

  for (const tenant of tenants) {
    const owing = monthsOwing(sales, tenant.id, now, SALES_DUE_DAY, {
      openedOn: tenantOpeningDate(tenant),
      closedOn: tenancyEndDate(tenant),
      reportsSales: tenantReportsSales(tenant),
    });
    if (owing.length === 0) continue;

    groups.push({
      id: tenant.id,
      name: tenantLabel(tenant) || "Unnamed tenant",
      tasks: owing.map((month) => ({
        id: `sales:${tenant.id}:${month.year}-${month.month}`,
        detail:
          // Due today is owed, not late. Saying "0 days late" of a month whose
          // deadline is this morning reads as an accusation and is wrong.
          month.daysLate === 0
            ? `${monthName(month.month)} ${month.year} sales — due today`
            : `${monthName(month.month)} ${month.year} sales — ${month.daysLate} day${
                month.daysLate === 1 ? "" : "s"
              } late`,
        tab: "overview" as TospTab,
      })),
    });
  }

  return groups.sort((a, b) => b.tasks.length - a.tasks.length);
}
